// /platform/company-profile — the facts the orio360.com website shows about the
// company (website plan D2, §6.2).
//
// One row. The website reads it live from GET /public/company-profile, and an
// empty box hides its line on the site, so the site never invents a fact.
// GSTIN shows only while "Show GSTIN on the website" is on — the API enforces
// that, the preview here just mirrors it.
//
// SUPER_ADMIN writes. ADMIN reads (the route gate is level 80); the controller
// enforces the same split, the UI only decides whether to render read-only.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  ErrorBoundary,
  Input,
  PageContainer,
  Skeleton,
  TextArea,
  Toggle,
  useToast,
} from '@bbpos/ui';
import { companyProfileService } from '@/services/company-profile.service';
import { ApiError } from '@/lib/api-client';
import { useAuthStore } from '@/stores/use-auth-store';
import { cn } from '@/constants/design-tokens';
import {
  SOCIAL_LINK_KEYS,
  type CompanyProfile,
  type SocialLinkKey,
  type UpdateCompanyProfileInput,
} from '@/types/company-profile';

const QUERY_KEY = ['platform-company-profile'];

const TEXT_FIELDS = [
  'brand_name',
  'legal_name',
  'founded_year',
  'address_line1',
  'address_line2',
  'city',
  'state',
  'pincode',
  'country',
  'phone',
  'whatsapp',
  'support_email',
  'business_hours',
  'story',
  'gstin',
  'pricing_note',
  'enquiry_email',
] as const;
type TextField = (typeof TEXT_FIELDS)[number];

/** NOT NULL columns: they can be changed, not emptied. */
const REQUIRED: TextField[] = [
  'brand_name',
  'country',
  'support_email',
  'enquiry_email',
  'pricing_note',
];

/** Column sizes from the migration. */
const MAX_LENGTH: Partial<Record<TextField, number>> = {
  brand_name: 80,
  legal_name: 160,
  address_line1: 160,
  address_line2: 160,
  city: 80,
  state: 80,
  pincode: 12,
  country: 80,
  support_email: 160,
  enquiry_email: 160,
  business_hours: 160,
  story: 4000,
  pricing_note: 300,
};

const SOCIAL_LABELS: Record<SocialLinkKey, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  youtube: 'YouTube',
  x: 'X',
};

const E164 = /^\+[1-9]\d{6,14}$/;
const GSTIN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

type Values = Record<TextField, string> & {
  send_enquiry_ack: boolean;
  show_gstin: boolean;
  social_links: Record<SocialLinkKey, string>;
};

/** Keyed by the API field name; social links as `social_links.<key>`. */
type FieldErrors = Record<string, string>;

function toValues(profile: CompanyProfile): Values {
  const text = Object.fromEntries(
    TEXT_FIELDS.map((field) => [field, profile[field] == null ? '' : String(profile[field])]),
  ) as Record<TextField, string>;
  const social = Object.fromEntries(
    SOCIAL_LINK_KEYS.map((key) => [key, profile.social_links?.[key] ?? '']),
  ) as Record<SocialLinkKey, string>;
  return {
    ...text,
    send_enquiry_ack: profile.send_enquiry_ack,
    show_gstin: profile.show_gstin,
    social_links: social,
  };
}

/** Every field is sent. An empty box becomes null, which hides its line on the website. */
function toPayload(values: Values): UpdateCompanyProfileInput {
  const text = (field: TextField) => {
    const value = values[field].trim();
    return value === '' ? null : value;
  };
  const year = values.founded_year.trim();
  const links: Partial<Record<SocialLinkKey, string>> = {};
  for (const key of SOCIAL_LINK_KEYS) {
    const link = values.social_links[key].trim();
    if (link) links[key] = link;
  }
  return {
    brand_name: values.brand_name.trim(),
    legal_name: text('legal_name'),
    founded_year: year === '' ? null : Number(year),
    address_line1: text('address_line1'),
    address_line2: text('address_line2'),
    city: text('city'),
    state: text('state'),
    pincode: text('pincode'),
    country: values.country.trim(),
    phone: text('phone'),
    whatsapp: text('whatsapp'),
    support_email: values.support_email.trim(),
    business_hours: text('business_hours'),
    story: text('story'),
    gstin: text('gstin'),
    show_gstin: values.show_gstin,
    pricing_note: values.pricing_note.trim(),
    social_links: links,
    enquiry_email: values.enquiry_email.trim(),
    send_enquiry_ack: values.send_enquiry_ack,
  };
}

/** The same rules as the API, so most mistakes show before saving. */
function validate(values: Values): FieldErrors {
  const errors: FieldErrors = {};
  for (const field of TEXT_FIELDS) {
    const value = values[field].trim();
    const max = MAX_LENGTH[field];
    if (REQUIRED.includes(field) && value === '') errors[field] = 'Please fill this in.';
    else if (max && value.length > max) errors[field] = `Use ${max} characters or fewer.`;
  }
  for (const field of ['phone', 'whatsapp'] as const) {
    const value = values[field].trim();
    if (value && !E164.test(value)) {
      errors[field] = 'Start with + and the country code, like +919876543210.';
    }
  }
  for (const field of ['support_email', 'enquiry_email'] as const) {
    const value = values[field].trim();
    if (value && !EMAIL.test(value))
      errors[field] = 'Type an email address, like name@example.com.';
  }
  const gstin = values.gstin.trim();
  if (gstin && !GSTIN.test(gstin)) {
    errors.gstin = 'A GSTIN has 15 letters and numbers, like 29ABCDE1234F1Z5.';
  }
  const year = values.founded_year.trim();
  if (year) {
    const n = Number(year);
    if (!Number.isInteger(n) || n < 1900 || n > new Date().getFullYear()) {
      errors.founded_year = 'Type a year from 1900 to this year.';
    }
  }
  for (const key of SOCIAL_LINK_KEYS) {
    const link = values.social_links[key].trim();
    if (link && !/^https:\/\/[^\s/]+\.[^\s]+$/.test(link)) {
      errors[`social_links.${key}`] = 'Paste the full link. It starts with https://';
    }
  }
  return errors;
}

/** API messages start with the field name ("phone must start with …"); show the rest. */
function errorsFromApi(err: ApiError): FieldErrors {
  const errors: FieldErrors = {};
  for (const { field, message } of err.details ?? []) {
    const rest = message.startsWith(field) ? message.slice(field.length).trim() : message;
    errors[field] = rest.charAt(0).toUpperCase() + rest.slice(1);
  }
  return errors;
}

function formatStamp(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString('en-IN');
}

export function CompanyProfilePage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const role = useAuthStore((s) => s.user?.role ?? s.profile?.role ?? '');
  const canEdit = role === 'SUPER_ADMIN';

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => companyProfileService.get(),
  });
  const lastSaved = formatStamp(data?.updated_at ?? null);

  const [values, setValues] = useState<Values | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});

  useEffect(() => {
    if (data) setValues(toValues(data));
  }, [data]);

  useEffect(() => {
    document.title = 'Company Profile — Orio360 Admin';
  }, []);

  const saveMutation = useMutation({
    mutationFn: (input: UpdateCompanyProfileInput) => companyProfileService.update(input),
    onSuccess: (saved) => {
      queryClient.setQueryData(QUERY_KEY, saved);
      setErrors({});
      toast({
        variant: 'success',
        title: 'Saved',
        description: 'The website shows the new details within 5 minutes.',
      });
    },
    onError: (err) => {
      const fieldErrors = err instanceof ApiError ? errorsFromApi(err) : {};
      setErrors(fieldErrors);
      toast({
        variant: 'error',
        title: 'Could not save',
        description:
          Object.keys(fieldErrors).length > 0
            ? 'Some boxes need a fix. Look for the red text.'
            : err instanceof ApiError
              ? err.message
              : 'Please try again.',
      });
    },
  });

  const setText = (field: TextField) => (value: string) =>
    setValues((prev) => (prev ? { ...prev, [field]: value } : prev));
  const setLink = (key: SocialLinkKey) => (value: string) =>
    setValues((prev) =>
      prev ? { ...prev, social_links: { ...prev.social_links, [key]: value } } : prev,
    );
  const setFlag = (field: 'send_enquiry_ack' | 'show_gstin') => (checked: boolean) =>
    setValues((prev) => (prev ? { ...prev, [field]: checked } : prev));

  const handleSave = () => {
    if (!values) return;
    const found = validate(values);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      toast({
        variant: 'warning',
        title: 'Some boxes need a fix',
        description: 'Look for the red text, fix it, then press Save again.',
      });
      return;
    }
    saveMutation.mutate(toPayload(values));
  };

  /** Props shared by every text box. */
  const box = (field: TextField, label: string, helperText?: string) => ({
    label,
    value: values?.[field] ?? '',
    onChange: setText(field),
    error: errors[field],
    helperText,
    disabled: !canEdit,
    required: REQUIRED.includes(field),
  });

  return (
    <ErrorBoundary>
      <PageContainer>
        {/* Hero */}
        <div className="relative mb-6 overflow-hidden rounded-2xl bg-gradient-to-r from-brand-600 via-indigo-600 to-violet-600 p-6 text-white shadow-lg animate-fade-in">
          <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-white/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-10 -left-10 h-40 w-40 rounded-full bg-white/5 blur-3xl" />
          <div className="relative">
            <div className="mb-1 text-[10px] uppercase tracking-[0.18em] text-indigo-100">
              Platform · Website
            </div>
            <h1 className="text-2xl font-bold text-white">Company Profile</h1>
            <p className="mt-1 max-w-2xl text-sm text-indigo-100">
              These are the facts the Orio360 website shows about us. Fill them in here, and the
              website shows them. Leave a box empty to hide that line on the website.
            </p>
          </div>
        </div>

        {!canEdit && (
          <div
            className="mb-6 flex items-start gap-3 rounded-xl border-l-4 border-brand-500 bg-brand-50 p-4 shadow-md dark:bg-brand-900/20"
            data-testid="company-profile-readonly-banner"
          >
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-brand-500 text-xs font-bold text-white">
              i
            </div>
            <div className="text-sm text-neutral-700 dark:text-neutral-300">
              <div className="font-semibold text-neutral-900 dark:text-white">Read only</div>
              You can look at this page, but you cannot change it. Only a super admin can save
              changes.
            </div>
          </div>
        )}

        {isLoading ? (
          <Skeleton className="h-96" />
        ) : isError || !values ? (
          <div className="rounded-xl border-l-4 border-danger-500 bg-danger-50 p-5 text-sm text-danger-700 shadow-md dark:bg-danger-900/20 dark:text-danger-300">
            <div className="mb-3 font-semibold">We could not load the Company Profile.</div>
            <Button variant="secondary" onClick={() => void refetch()}>
              Try again
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="space-y-6 lg:col-span-2">
              <Section title="Business details" tone="brand" icon={ICONS.building}>
                <Input {...box('brand_name', 'Brand name', 'The name people know us by.')} />
                <Input {...box('legal_name', 'Legal name', 'The name on our company papers.')} />
                <Input
                  {...box('founded_year', 'Year we started', 'For example, 2024.')}
                  inputMode="numeric"
                />
              </Section>

              <Section title="Address" tone="teal" icon={ICONS.pin}>
                <Input {...box('address_line1', 'Address line 1')} />
                <Input {...box('address_line2', 'Address line 2')} />
                <Input {...box('city', 'City')} />
                <Input {...box('state', 'State')} />
                <Input {...box('pincode', 'PIN code')} inputMode="numeric" />
                <Input {...box('country', 'Country')} />
              </Section>

              <Section title="Contact" tone="violet" icon={ICONS.phone}>
                <Input
                  {...box('phone', 'Phone', 'Start with + and the country code.')}
                  type="tel"
                  placeholder="+919876543210"
                />
                <Input
                  {...box('whatsapp', 'WhatsApp number', 'Start with + and the country code.')}
                  type="tel"
                  placeholder="+919876543210"
                />
                <Input
                  {...box(
                    'support_email',
                    'Support email',
                    'People write to this address for help.',
                  )}
                  type="email"
                />
                <Input
                  {...box('business_hours', 'Opening hours')}
                  placeholder="Monday to Saturday, 9 am to 7 pm"
                />
              </Section>

              <Section
                title="About text"
                tone="indigo"
                icon={ICONS.book}
                hint="Shown on the About page."
                wide
              >
                <TextArea
                  label="Our story"
                  value={values.story}
                  onChange={setText('story')}
                  error={errors.story}
                  disabled={!canEdit}
                  maxLength={4000}
                  rows={7}
                />
              </Section>

              <Section title="Tax" tone="warning" icon={ICONS.receipt}>
                <Input
                  {...box('gstin', 'GSTIN', '15 letters and numbers.')}
                  onChange={(value) => setText('gstin')(value.toUpperCase())}
                  placeholder="29ABCDE1234F1Z5"
                />
                <div className="flex items-center">
                  <Toggle
                    label="Show GSTIN on the website"
                    description="When this is off, the website does not show the GSTIN."
                    checked={values.show_gstin}
                    onChange={setFlag('show_gstin')}
                    disabled={!canEdit}
                  />
                </div>
              </Section>

              <Section title="Website text" tone="pink" icon={ICONS.text} wide>
                <Input
                  {...box(
                    'pricing_note',
                    'Price note',
                    'Shown under the prices on the Pricing page.',
                  )}
                />
              </Section>

              <Section
                title="Social links"
                tone="sky"
                icon={ICONS.link}
                hint="Paste the full link to each page. It starts with https://"
              >
                {SOCIAL_LINK_KEYS.map((key) => (
                  <Input
                    key={key}
                    label={SOCIAL_LABELS[key]}
                    value={values.social_links[key]}
                    onChange={setLink(key)}
                    error={errors[`social_links.${key}`]}
                    disabled={!canEdit}
                    placeholder="https://"
                  />
                ))}
              </Section>

              <Section title="Enquiry emails" tone="success" icon={ICONS.mail}>
                <Input
                  {...box(
                    'enquiry_email',
                    'Enquiry email',
                    'Messages from the website form go to this address.',
                  )}
                  type="email"
                />
                <div className="flex items-center">
                  <Toggle
                    label="Send a thank-you email"
                    description="When someone writes to us, send them a short “we got your message” email."
                    checked={values.send_enquiry_ack}
                    onChange={setFlag('send_enquiry_ack')}
                    disabled={!canEdit}
                  />
                </div>
              </Section>

              {canEdit && (
                <div className="flex flex-wrap items-center gap-3 rounded-xl border border-neutral-100 bg-white p-5 shadow-md dark:border-neutral-700 dark:bg-neutral-800">
                  <div className="mr-auto text-sm text-neutral-600 dark:text-neutral-300">
                    {lastSaved
                      ? `Last saved ${lastSaved}`
                      : 'Not saved yet. The website shows the starting values.'}
                  </div>
                  <Button onClick={handleSave} loading={saveMutation.isPending}>
                    Save changes
                  </Button>
                </div>
              )}
            </div>

            <div className="lg:col-span-1">
              <div className="lg:sticky lg:top-6">
                <ContactPreview values={values} />
              </div>
            </div>
          </div>
        )}
      </PageContainer>
    </ErrorBoundary>
  );
}

// ─── Section card ───────────────────────────────────────────────────────────

const TONES = {
  brand: { card: 'bg-brand-50/60 dark:bg-brand-900/10', icon: 'from-brand-500 to-indigo-500' },
  teal: { card: 'bg-teal-50/60 dark:bg-teal-900/10', icon: 'from-teal-500 to-success-500' },
  violet: { card: 'bg-violet-50/60 dark:bg-violet-900/10', icon: 'from-violet-500 to-pink-500' },
  indigo: { card: 'bg-indigo-50/60 dark:bg-indigo-900/10', icon: 'from-indigo-500 to-violet-500' },
  warning: {
    card: 'bg-warning-50/60 dark:bg-warning-900/10',
    icon: 'from-warning-500 to-danger-500',
  },
  pink: { card: 'bg-pink-50/60 dark:bg-pink-900/10', icon: 'from-pink-500 to-violet-500' },
  sky: { card: 'bg-sky-50/60 dark:bg-sky-900/10', icon: 'from-sky-500 to-brand-500' },
  success: {
    card: 'bg-success-50/60 dark:bg-success-900/10',
    icon: 'from-success-500 to-teal-500',
  },
} as const;

function Section({
  title,
  tone,
  icon,
  hint,
  wide = false,
  children,
}: {
  title: string;
  tone: keyof typeof TONES;
  icon: string;
  hint?: string;
  /** One column instead of two. */
  wide?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section
      className={cn(
        'rounded-xl border border-neutral-100 p-5 shadow-md transition-all duration-200 hover:shadow-lg animate-slide-up dark:border-neutral-700',
        TONES[tone].card,
      )}
    >
      <div className="mb-4 flex items-center gap-3">
        <div
          className={cn(
            'flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br text-white',
            TONES[tone].icon,
          )}
          aria-hidden="true"
        >
          <Glyph d={icon} />
        </div>
        <div>
          <h2 className="text-base font-semibold text-neutral-900 dark:text-white">{title}</h2>
          {hint && <p className="text-xs text-neutral-500 dark:text-neutral-400">{hint}</p>}
        </div>
      </div>
      <div className={cn('grid grid-cols-1 gap-4', !wide && 'md:grid-cols-2')}>{children}</div>
    </section>
  );
}

// ─── Live preview of the website's contact box ──────────────────────────────

function ContactPreview({ values }: { values: Values }) {
  const v = (field: TextField) => values[field].trim();
  const place = [v('city'), v('state')].filter(Boolean).join(', ');
  const addressLines = [
    v('address_line1'),
    v('address_line2'),
    [place, v('pincode')].filter(Boolean).join(' '),
    v('country'),
  ].filter(Boolean);
  const links = SOCIAL_LINK_KEYS.filter((key) => values.social_links[key].trim());
  const gstin = values.show_gstin ? v('gstin') : '';

  return (
    <div
      className="overflow-hidden rounded-xl border border-neutral-100 bg-white shadow-md transition-all duration-200 hover:shadow-lg dark:border-neutral-700 dark:bg-neutral-800"
      data-testid="company-profile-preview"
    >
      <div className="bg-gradient-to-r from-brand-600 to-indigo-600 px-5 py-3 text-white">
        <div className="text-[10px] uppercase tracking-[0.18em] text-indigo-100">Preview</div>
        <div className="text-sm font-semibold">The contact box on the website</div>
      </div>
      <div className="space-y-4 p-5 text-sm text-neutral-700 dark:text-neutral-300">
        <div>
          <div className="text-lg font-bold text-neutral-900 dark:text-white">
            {v('brand_name') || 'Orio360'}
          </div>
          {v('legal_name') && (
            <div className="text-xs text-neutral-500 dark:text-neutral-400">{v('legal_name')}</div>
          )}
        </div>

        {addressLines.length > 0 && (
          <PreviewRow icon={ICONS.pin} label="Address">
            {addressLines.map((line) => (
              <div key={line}>{line}</div>
            ))}
          </PreviewRow>
        )}
        {v('phone') && (
          <PreviewRow icon={ICONS.phone} label="Phone">
            {v('phone')}
          </PreviewRow>
        )}
        {v('whatsapp') && (
          <PreviewRow icon={ICONS.chat} label="WhatsApp">
            {v('whatsapp')}
          </PreviewRow>
        )}
        {v('support_email') && (
          <PreviewRow icon={ICONS.mail} label="Email">
            <span className="break-all">{v('support_email')}</span>
          </PreviewRow>
        )}
        {v('business_hours') && (
          <PreviewRow icon={ICONS.clock} label="Open">
            {v('business_hours')}
          </PreviewRow>
        )}
        {gstin && (
          <PreviewRow icon={ICONS.receipt} label="GSTIN">
            <span className="font-mono">{gstin}</span>
          </PreviewRow>
        )}
        {links.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1">
            {links.map((key) => (
              <span
                key={key}
                className="rounded-full bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700 dark:bg-brand-900/30 dark:text-brand-300"
              >
                {SOCIAL_LABELS[key]}
              </span>
            ))}
          </div>
        )}

        <p className="border-t border-neutral-100 pt-3 text-xs text-neutral-500 dark:border-neutral-700 dark:text-neutral-400">
          Empty boxes are hidden on the website.
        </p>
      </div>
    </div>
  );
}

function PreviewRow({
  icon,
  label,
  children,
}: {
  icon: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <div
        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-300"
        aria-hidden="true"
      >
        <Glyph d={icon} small />
      </div>
      <div>
        <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400">{label}</div>
        <div className="text-neutral-900 dark:text-white">{children}</div>
      </div>
    </div>
  );
}

// ─── Icons ──────────────────────────────────────────────────────────────────

const ICONS = {
  building: 'M3 21h18M5 21V7l7-4 7 4v14M9 9h1m4 0h1M9 13h1m4 0h1M9 17h1m4 0h1',
  pin: 'M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z',
  phone:
    'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
  chat: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
  mail: 'M3 8l9 6 9-6M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  clock: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  book: 'M12 6.253v13M12 6.253C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
  receipt:
    'M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z',
  text: 'M4 6h16M4 12h16M4 18h7',
  link: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1',
} as const;

function Glyph({ d, small = false }: { d: string; small?: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={small ? 'h-4 w-4' : 'h-5 w-5'}
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}
